import type { InspectionDraftInput } from './guided-inspection';
import { GUIDED_INSPECTION_LIMITS } from './guided-inspection';

export type OfflineInspectionOriginalState = 'pending' | 'error' | 'synced';
export type OfflineInspectionState = 'pending' | 'error' | 'synced';
export type OfflineInspectionServer = { draftId: string | null; version: number | null; vehicleVersion: number | null };
export type OfflineInspectionUpload = { assetId: string; sha256: string; mimeType: string; size: number };
export type OfflineInspectionAck = { assetId: string; draftId: string; draftVersion: number };
export type OfflineInspectionOriginal = {
  clientMediaId: string;
  blob: Blob;
  upload: OfflineInspectionUpload | null;
  ack: OfflineInspectionAck | null;
  state: OfflineInspectionOriginalState;
  lastError: string | null;
};
export type OfflineInspectionOperation =
  | { kind: 'save'; key: string; input: InspectionDraftInput; expectedVersion: number | null }
  | { kind: 'upload'; key: string; clientMediaId: string; vehicleId: string; filename: string; mimeType: string }
  | { kind: 'resolve-media'; key: string; draftId: string; clientMediaId: string; assetId: string; expectedVersion: number }
  | { kind: 'commit'; key: string; draftId: string; expectedVersion: number; expectedVehicleVersion: number };
export type OfflineInspectionWrite = {
  actorId: string;
  clientDraftId: string;
  input: InspectionDraftInput;
  originals: OfflineInspectionOriginal[];
  server: OfflineInspectionServer;
  state: OfflineInspectionState;
  lastError: string | null;
  operation: OfflineInspectionOperation | null;
};
export type StoredOfflineInspection = OfflineInspectionWrite & { revision: number; updatedAt: string };

export class InspectionOfflineStorageError extends Error {
  readonly code: 'UNAVAILABLE' | 'BLOCKED' | 'QUOTA' | 'INVALID' | 'CONFLICT' | 'STORAGE';
  constructor(code: InspectionOfflineStorageError['code'], message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = 'InspectionOfflineStorageError';
    this.code = code;
  }
}

const DATABASE_NAME = 'way-guided-inspections-v1';
const DATABASE_VERSION = 1;
const OBJECT_STORE = 'inspections';
const OPERATION_KEY_MIN = 8;
const OPERATION_KEY_MAX = 200;
type LooseRecord = Record<string, unknown>;

function invalid(message: string): never { throw new InspectionOfflineStorageError('INVALID', message); }
function conflict(message: string): never { throw new InspectionOfflineStorageError('CONFLICT', message); }
function asRecord(value: unknown, name: string): LooseRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) invalid(`${name} must be an object`);
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) invalid(`${name} must be a plain object`);
  return value as LooseRecord;
}
function exactKeys(value: LooseRecord, keys: readonly string[], name: string): void {
  const allowed = new Set(keys);
  for (const key of Object.keys(value)) if (!allowed.has(key)) invalid(`${name} contains unsupported field ${key}`);
}
function id(value: unknown, name: string): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > GUIDED_INSPECTION_LIMITS.id || value.trim() !== value || /[\u0000-\u001f\u007f]/u.test(value)) invalid(`${name} must be a non-blank identifier`);
  return value;
}
function revision(value: unknown, name: string, nullable = false): number | null {
  if (nullable && value === null) return null;
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) invalid(`${name} must be a positive integer${nullable ? ' or null' : ''}`);
  return value;
}
function isStringOrNull(value: unknown, name: string, max = 1000): string | null {
  if (value === null) return null;
  if (typeof value !== 'string' || value.length > max) invalid(`${name} must be a string of at most ${max} characters or null`);
  return value;
}
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const recordValue = value as LooseRecord;
  return `{${Object.keys(recordValue).sort().map(key => `${JSON.stringify(key)}:${canonical(recordValue[key])}`).join(',')}}`;
}
function sameOperation(a: OfflineInspectionOperation, b: OfflineInspectionOperation): boolean { return canonical(a) === canonical(b); }
function validKey(value: unknown): string {
  if (typeof value !== 'string' || value.length < OPERATION_KEY_MIN || value.length > OPERATION_KEY_MAX || value.trim() !== value) invalid(`Operation key must be ${OPERATION_KEY_MIN} to ${OPERATION_KEY_MAX} characters`);
  return value;
}
function validateOperation(value: unknown, input: InspectionDraftInput): OfflineInspectionOperation {
  const raw = asRecord(value, 'operation');
  if (typeof raw.kind !== 'string') invalid('operation.kind is required');
  validKey(raw.key);
  if (raw.kind === 'save') {
    exactKeys(raw, ['kind','key','input','expectedVersion'], 'operation');
    const opInput = asRecord(raw.input, 'operation.input');
    if (opInput.clientDraftId !== input.clientDraftId || opInput.vehicleId !== input.vehicleId) invalid('Save operation must target this inspection and vehicle');
    return { kind: 'save', key: raw.key as string, input: raw.input as InspectionDraftInput, expectedVersion: revision(raw.expectedVersion, 'operation.expectedVersion', true) };
  }
  if (raw.kind === 'upload') {
    exactKeys(raw, ['kind','key','clientMediaId','vehicleId','filename','mimeType'], 'operation');
    return { kind: 'upload', key: raw.key as string, clientMediaId: id(raw.clientMediaId, 'operation.clientMediaId'), vehicleId: id(raw.vehicleId, 'operation.vehicleId'), filename: id(raw.filename, 'operation.filename'), mimeType: id(raw.mimeType, 'operation.mimeType') };
  }
  if (raw.kind === 'resolve-media') {
    exactKeys(raw, ['kind','key','draftId','clientMediaId','assetId','expectedVersion'], 'operation');
    return { kind: 'resolve-media', key: raw.key as string, draftId: id(raw.draftId, 'operation.draftId'), clientMediaId: id(raw.clientMediaId, 'operation.clientMediaId'), assetId: id(raw.assetId, 'operation.assetId'), expectedVersion: revision(raw.expectedVersion, 'operation.expectedVersion')! };
  }
  if (raw.kind === 'commit') {
    exactKeys(raw, ['kind','key','draftId','expectedVersion','expectedVehicleVersion'], 'operation');
    return { kind: 'commit', key: raw.key as string, draftId: id(raw.draftId, 'operation.draftId'), expectedVersion: revision(raw.expectedVersion, 'operation.expectedVersion')!, expectedVehicleVersion: revision(raw.expectedVehicleVersion, 'operation.expectedVehicleVersion')! };
  }
  invalid('operation.kind is unsupported');
}

function validateWrite(value: OfflineInspectionWrite): OfflineInspectionWrite {
  const write = asRecord(value, 'inspection');
  exactKeys(write, ['actorId','clientDraftId','input','originals','server','state','lastError','operation'], 'inspection');
  const actorId = id(write.actorId, 'actorId');
  const clientDraftId = id(write.clientDraftId, 'clientDraftId');
  const inputRecord = asRecord(write.input, 'input');
  exactKeys(inputRecord, ['clientDraftId','vehicleId','notes','dictationText','checklist','tires','damage','media'], 'input');
  const input = inputRecord as unknown as InspectionDraftInput;
  if (input.clientDraftId !== clientDraftId) invalid('input.clientDraftId must match clientDraftId');
  if (typeof input.vehicleId !== 'string' || input.vehicleId.length === 0) invalid('input.vehicleId is required');
  if (!Array.isArray(input.media) || input.media.length > GUIDED_INSPECTION_LIMITS.media) invalid(`input.media must have at most ${GUIDED_INSPECTION_LIMITS.media} entries`);
  const descriptors = new Map<string, { clientId: string; filename: string; mimeType: string; size: number; sha256: string }>();
  for (const [index, descriptorValue] of input.media.entries()) {
    const descriptor = asRecord(descriptorValue, `input.media[${index}]`);
    exactKeys(descriptor, ['clientId','filename','mimeType','size','sha256'], `input.media[${index}]`);
    const clientId = id(descriptor.clientId, `input.media[${index}].clientId`);
    if (descriptors.has(clientId)) invalid(`input.media contains duplicate clientId ${clientId}`);
    if (typeof descriptor.filename !== 'string' || descriptor.filename.length === 0 || descriptor.filename.length > GUIDED_INSPECTION_LIMITS.label) invalid(`input.media[${index}].filename is invalid`);
    if (typeof descriptor.mimeType !== 'string' || descriptor.mimeType.length === 0) invalid(`input.media[${index}].mimeType is invalid`);
    if (typeof descriptor.size !== 'number' || !Number.isSafeInteger(descriptor.size) || descriptor.size < 1) invalid(`input.media[${index}].size is invalid`);
    if (typeof descriptor.sha256 !== 'string' || !/^[a-f0-9]{64}$/iu.test(descriptor.sha256)) invalid(`input.media[${index}].sha256 is invalid`);
    descriptors.set(clientId, { clientId, filename: descriptor.filename, mimeType: descriptor.mimeType, size: descriptor.size, sha256: descriptor.sha256.toLowerCase() });
  }
  if (!Array.isArray(write.originals) || write.originals.length > GUIDED_INSPECTION_LIMITS.media) invalid(`originals must have at most ${GUIDED_INSPECTION_LIMITS.media} entries`);
  const originals: OfflineInspectionOriginal[] = [];
  const originalIds = new Set<string>();
  for (const [index, originalValue] of write.originals.entries()) {
    const original = asRecord(originalValue, `originals[${index}]`);
    exactKeys(original, ['clientMediaId','blob','upload','ack','state','lastError'], `originals[${index}]`);
    const clientMediaId = id(original.clientMediaId, `originals[${index}].clientMediaId`);
    if (originalIds.has(clientMediaId)) invalid(`originals contains duplicate clientMediaId ${clientMediaId}`);
    originalIds.add(clientMediaId);
    const descriptor = descriptors.get(clientMediaId);
    if (typeof Blob === 'undefined' || !(original.blob instanceof Blob)) invalid(`originals[${index}].blob must be a browser Blob or File`);
    const blob = original.blob as Blob;
    if (descriptor && (blob.size !== descriptor.size || blob.type !== descriptor.mimeType)) invalid(`Original ${clientMediaId} bytes must match the declared size and MIME type`);
    if (typeof original.state !== 'string' || !['pending','error','synced'].includes(original.state)) invalid(`originals[${index}].state is invalid`);
    const state = original.state as OfflineInspectionOriginalState;
    const lastError = isStringOrNull(original.lastError, `originals[${index}].lastError`);
    const upload = validateUpload(original.upload, descriptor, blob, index);
    const ack = validateAck(original.ack, upload, write.server, index);
    if (state === 'synced' && (!upload || !ack || upload.assetId !== ack.assetId)) invalid(`Synced original ${clientMediaId} needs matching upload and acknowledgement receipts`);
    if (state === 'error' && !lastError?.trim()) invalid(`Failed original ${clientMediaId} needs a visible error`);
    if (state !== 'error' && lastError !== null) invalid(`Original ${clientMediaId} can carry lastError only in error state`);
    originals.push({ clientMediaId, blob, upload, ack, state, lastError });
  }
  for (const clientId of descriptors.keys()) if (!originalIds.has(clientId)) invalid(`Media ${clientId} has no retained original`);
  const server = validateServer(write.server);
  const stateValue = write.state;
  if (stateValue !== 'pending' && stateValue !== 'error' && stateValue !== 'synced') invalid('state is invalid');
  const lastError = isStringOrNull(write.lastError, 'lastError');
  const operation = write.operation === null ? null : validateOperation(write.operation, input);
  if (operation?.kind === 'upload') {
    const descriptor = descriptors.get(operation.clientMediaId);
    if (operation.vehicleId !== input.vehicleId || !descriptor || operation.filename !== descriptor.filename || operation.mimeType !== descriptor.mimeType) invalid('Upload operation must match this vehicle and its media metadata');
  }
  if (operation?.kind === 'resolve-media' && (server.draftId !== operation.draftId || !originals.some(item => item.clientMediaId === operation.clientMediaId && item.upload?.assetId === operation.assetId))) invalid('Resolve operation must match the uploaded asset and server draft');
  if (operation?.kind === 'commit' && server.draftId !== operation.draftId) invalid('Commit operation must target the current server draft');
  if (stateValue === 'error' && !lastError?.trim()) invalid('An error inspection needs a visible lastError');
  if (stateValue !== 'error' && lastError !== null) invalid('lastError is allowed only in error state');
  if (stateValue === 'synced') {
    const referencedIds = [...descriptors.keys()];
    if (!server.draftId || !server.version || !server.vehicleVersion || operation || lastError !== null || referencedIds.some(clientId => originals.find(original => original.clientMediaId === clientId)?.state !== 'synced')) invalid('A synced inspection needs server versions, no pending operation or error, and synced referenced originals');
  }
  return { actorId, clientDraftId, input: write.input as InspectionDraftInput, originals, server, state: stateValue, lastError, operation };
}

function validateUpload(value: unknown, descriptor: { sha256: string; mimeType: string; size: number } | undefined, blob: Blob, index: number): OfflineInspectionUpload | null {
  if (value === null) return null;
  const raw = asRecord(value, `originals[${index}].upload`);
  exactKeys(raw, ['assetId','sha256','mimeType','size'], `originals[${index}].upload`);
  const assetId = id(raw.assetId, `originals[${index}].upload.assetId`);
  if (typeof raw.sha256 !== 'string' || !/^[a-f0-9]{64}$/iu.test(raw.sha256) || typeof raw.mimeType !== 'string' || !Number.isSafeInteger(raw.size) || raw.size !== blob.size || raw.mimeType !== blob.type || (descriptor && (raw.sha256.toLowerCase() !== descriptor.sha256 || raw.mimeType !== descriptor.mimeType || raw.size !== descriptor.size))) invalid(`Upload receipt for ${assetId} does not match the retained original`);
  return { assetId, sha256: raw.sha256, mimeType: raw.mimeType, size: raw.size as number };
}
function validateServer(value: unknown): OfflineInspectionServer {
  const raw = asRecord(value, 'server');
  exactKeys(raw, ['draftId','version','vehicleVersion'], 'server');
  const draftId = raw.draftId === null ? null : id(raw.draftId, 'server.draftId');
  const version = revision(raw.version, 'server.version', true);
  const vehicleVersion = revision(raw.vehicleVersion, 'server.vehicleVersion', true);
  if ((draftId === null) !== (version === null)) invalid('server draftId and version must be set or null together');
  return { draftId, version, vehicleVersion };
}
function validateAck(value: unknown, upload: OfflineInspectionUpload | null, serverValue: unknown, index: number): OfflineInspectionAck | null {
  if (value === null) return null;
  const raw = asRecord(value, `originals[${index}].ack`);
  exactKeys(raw, ['assetId','draftId','draftVersion'], `originals[${index}].ack`);
  const server = validateServer(serverValue);
  const assetId = id(raw.assetId, `originals[${index}].ack.assetId`);
  const draftId = id(raw.draftId, `originals[${index}].ack.draftId`);
  const draftVersion = revision(raw.draftVersion, `originals[${index}].ack.draftVersion`)!;
  if (!upload || upload.assetId !== assetId || server.draftId !== draftId || server.version === null || draftVersion > server.version) invalid('Acknowledgement must match its uploaded asset and server draft');
  return { assetId, draftId, draftVersion };
}

function idbError(error: unknown, action: string): InspectionOfflineStorageError {
  if (error instanceof InspectionOfflineStorageError) return error;
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'QuotaExceededError') return new InspectionOfflineStorageError('QUOTA', `Browser storage quota was exceeded while ${action}`, { cause: error });
  if (name === 'InvalidStateError' || name === 'SecurityError' || name === 'NotSupportedError') return new InspectionOfflineStorageError('UNAVAILABLE', `IndexedDB is unavailable while ${action}`, { cause: error });
  return new InspectionOfflineStorageError('STORAGE', `IndexedDB could not complete ${action}`, { cause: error });
}

export class InspectionOfflineStore {
  private database: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    if (this.database) return Promise.resolve(this.database);
    if (this.opening) return this.opening;
    if (typeof indexedDB === 'undefined') return Promise.reject(new InspectionOfflineStorageError('UNAVAILABLE', 'This browser does not provide IndexedDB'));
    let request: IDBOpenDBRequest;
    try { request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION); }
    catch (error) { return Promise.reject(idbError(error, 'opening the inspection store')); }
    const opening = new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(OBJECT_STORE)) {
          const store = db.createObjectStore(OBJECT_STORE, { keyPath: ['actorId','clientDraftId'] });
          store.createIndex('actorId', 'actorId', { unique: false });
        }
      };
      request.onerror = () => { if (!settled) { settled = true; reject(idbError(request.error, 'opening the inspection store')); } };
      request.onblocked = () => { if (!settled) { settled = true; reject(new InspectionOfflineStorageError('BLOCKED', 'Another tab is blocking the inspection store upgrade')); } };
      request.onsuccess = () => {
        const db = request.result;
        if (settled) { db.close(); return; }
        settled = true; this.database = db;
        db.onversionchange = () => { db.close(); if (this.database === db) this.database = null; };
        resolve(db);
      };
    });
    this.opening = opening;
    void opening.then(() => { if (this.opening === opening) this.opening = null; }, () => { if (this.opening === opening) this.opening = null; });
    return opening;
  }

  private async read(actorId: string, clientDraftId: string): Promise<StoredOfflineInspection | null> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      let value: StoredOfflineInspection | null = null;
      let requestError: unknown;
      let transaction: IDBTransaction;
      try { transaction = db.transaction(OBJECT_STORE, 'readonly'); }
      catch (error) { reject(idbError(error, 'reading an inspection')); return; }
      const request = transaction.objectStore(OBJECT_STORE).get([actorId, clientDraftId]);
      request.onsuccess = () => { value = (request.result as StoredOfflineInspection | undefined) ?? null; };
      request.onerror = () => { requestError = request.error; };
      transaction.oncomplete = () => resolve(value);
      transaction.onabort = () => reject(idbError(requestError ?? transaction.error, 'reading an inspection'));
      transaction.onerror = () => { requestError ??= transaction.error; };
    });
  }

  async get(actorId: string, clientDraftId: string): Promise<StoredOfflineInspection | null> {
    return this.read(id(actorId, 'actorId'), id(clientDraftId, 'clientDraftId'));
  }

  async list(actorId: string): Promise<StoredOfflineInspection[]> {
    const actor = id(actorId, 'actorId');
    const db = await this.open();
    return new Promise((resolve, reject) => {
      let values: StoredOfflineInspection[] = [];
      let requestError: unknown;
      let transaction: IDBTransaction;
      try { transaction = db.transaction(OBJECT_STORE, 'readonly'); }
      catch (error) { reject(idbError(error, 'listing inspections')); return; }
      const request = transaction.objectStore(OBJECT_STORE).index('actorId').getAll(actor);
      request.onsuccess = () => { values = (request.result as StoredOfflineInspection[]).sort((a,b) => b.updatedAt.localeCompare(a.updatedAt) || a.clientDraftId.localeCompare(b.clientDraftId)); };
      request.onerror = () => { requestError = request.error; };
      transaction.oncomplete = () => resolve(values);
      transaction.onabort = () => reject(idbError(requestError ?? transaction.error, 'listing inspections'));
      transaction.onerror = () => { requestError ??= transaction.error; };
    });
  }

  async put(recordValue: OfflineInspectionWrite, expectedRevision: number | null): Promise<StoredOfflineInspection> {
    let write: OfflineInspectionWrite;
    try { write = validateWrite(structuredClone(recordValue)); }
    catch (error) {
      if (error instanceof InspectionOfflineStorageError) throw error;
      throw new InspectionOfflineStorageError('INVALID', 'Inspection data cannot be safely copied for IndexedDB storage', { cause: error });
    }
    const expected = revision(expectedRevision, 'expectedRevision', true);
    const prior = await this.read(write.actorId, write.clientDraftId);
    if ((expected === null && prior !== null) || (expected !== null && (prior === null || prior.revision !== expected))) conflict('Inspection local revision changed; reload it before saving');
    await this.verifyImmutableOriginals(prior, write);
    this.verifyOperationTransition(prior, write);

    const db = await this.open();
    return new Promise((resolve, reject) => {
      let result: StoredOfflineInspection | null = null;
      let controlledError: InspectionOfflineStorageError | null = null;
      let requestError: unknown;
      let transaction: IDBTransaction;
      try { transaction = db.transaction(OBJECT_STORE, 'readwrite'); }
      catch (error) { reject(idbError(error, 'saving an inspection')); return; }
      const store = transaction.objectStore(OBJECT_STORE);
      const request = store.get([write.actorId, write.clientDraftId]);
      request.onsuccess = () => {
        const current = (request.result as StoredOfflineInspection | undefined) ?? null;
        if ((expected === null && current !== null) || (expected !== null && (current === null || current.revision !== expected))) {
          controlledError = new InspectionOfflineStorageError('CONFLICT', 'Inspection local revision changed during save; no data was overwritten');
          transaction.abort(); return;
        }
        result = { ...write, revision: (current?.revision ?? 0) + 1, updatedAt: new Date().toISOString() };
        try { store.put(result); }
        catch (error) {
          controlledError = idbError(error, 'saving an inspection');
          try { transaction.abort(); } catch { /* the transaction may already have aborted */ }
        }
      };
      request.onerror = () => { requestError = request.error; };
      transaction.oncomplete = () => { if (result) resolve(result); else reject(new InspectionOfflineStorageError('STORAGE', 'Inspection save completed without a stored record')); };
      transaction.onabort = () => reject(controlledError ?? idbError(requestError ?? transaction.error, 'saving an inspection'));
      transaction.onerror = () => { requestError ??= transaction.error; };
    });
  }

  private async verifyImmutableOriginals(prior: StoredOfflineInspection | null, next: OfflineInspectionWrite): Promise<void> {
    if (!prior) {
      const descriptors = new Set(next.input.media.map(item=>item.clientId));
      if (next.originals.some(original=>!descriptors.has(original.clientMediaId))) invalid('A new inspection can store originals only when their media descriptors are present');
      return;
    }
    const oldDescriptors = new Map(prior.input.media.map(item => [item.clientId,item]));
    const newDescriptors = new Map(next.input.media.map(item => [item.clientId,item]));
    const oldOriginals = new Map(prior.originals.map(original => [original.clientMediaId,original]));
    const newOriginals = new Map(next.originals.map(original => [original.clientMediaId,original]));
    for (const [clientMediaId, oldOriginal] of oldOriginals) {
      const oldDescriptor = oldDescriptors.get(clientMediaId), newDescriptor = newDescriptors.get(clientMediaId), newOriginal = newOriginals.get(clientMediaId);
      if (!newOriginal) conflict(`Original ${clientMediaId} is retained until the whole inspection is explicitly deleted`);
      if (oldDescriptor && newDescriptor && canonical(oldDescriptor) !== canonical(newDescriptor)) conflict(`Media metadata for ${clientMediaId} is immutable; use a new clientMediaId`);
      if (!oldDescriptor && newDescriptor) conflict(`Media metadata for ${clientMediaId} cannot be reintroduced; use a new clientMediaId`);
      const [oldBytes,newBytes] = await Promise.all([oldOriginal.blob.arrayBuffer(),newOriginal.blob.arrayBuffer()]);
      if (!this.bytesEqual(oldBytes,newBytes)) conflict(`Original bytes for ${clientMediaId} are immutable; use a new clientMediaId`);
      if (oldOriginal.upload && canonical(oldOriginal.upload) !== canonical(newOriginal.upload)) conflict(`Upload receipt for ${clientMediaId} is immutable`);
      if (oldOriginal.ack && canonical(oldOriginal.ack) !== canonical(newOriginal.ack)) conflict(`Acknowledgement for ${clientMediaId} is immutable`);
      if (oldOriginal.state === 'synced' && newOriginal.state !== 'synced') conflict(`Synced original ${clientMediaId} cannot return to a pending state`);
    }
  }

  private bytesEqual(a: ArrayBuffer, b: ArrayBuffer): boolean {
    if (a.byteLength !== b.byteLength) return false;
    const left = new Uint8Array(a), right = new Uint8Array(b);
    for (let index=0; index<left.length; index++) if (left[index] !== right[index]) return false;
    return true;
  }

  private verifyOperationTransition(prior: StoredOfflineInspection | null, next: OfflineInspectionWrite): void {
    if (!prior?.operation) return;
    if (next.operation && sameOperation(prior.operation,next.operation)) return;
    if (next.operation) conflict('An outstanding operation cannot be replaced before its outcome is recorded');
    // Clearing is explicit caller orchestration after it has recorded the reply.
  }

  async delete(actorId: string, clientDraftId: string, expectedRevision: number): Promise<void> {
    const actor = id(actorId,'actorId'), draftId = id(clientDraftId,'clientDraftId'), expected = revision(expectedRevision,'expectedRevision')!;
    const db = await this.open();
    await new Promise<void>((resolve,reject)=>{
      let controlledError: InspectionOfflineStorageError|null=null;
      let requestError: unknown;
      let transaction: IDBTransaction;
      try { transaction=db.transaction(OBJECT_STORE,'readwrite'); }
      catch(error){reject(idbError(error,'deleting an inspection'));return;}
      const store=transaction.objectStore(OBJECT_STORE),request=store.get([actor,draftId]);
      request.onsuccess=()=>{
        const current=(request.result as StoredOfflineInspection|undefined)??null;
        if(!current||current.revision!==expected){controlledError=new InspectionOfflineStorageError('CONFLICT','Inspection local revision changed; it was not deleted');transaction.abort();return;}
        try { store.delete([actor,draftId]); }
        catch(error){controlledError=idbError(error,'deleting an inspection');try{transaction.abort();}catch{/* already aborted */}}
      };
      request.onerror=()=>{requestError=request.error;};
      transaction.oncomplete=()=>resolve();
      transaction.onabort=()=>reject(controlledError??idbError(requestError??transaction.error,'deleting an inspection'));
      transaction.onerror=()=>{requestError??=transaction.error;};
    });
  }

  close(): void {
    this.database?.close();
    this.database = null;
    this.opening = null;
  }
}
